import React, { useEffect, useState } from 'react';

import { NotificationsOutlined } from '@material-ui/icons';

import PageLoading from '../../../../components/PageLoading';
import NotificationsSkeleton from '../../../../components/PageLoading/skeletons/NotificationsSkeleton';
import { useWishlist } from '../../../../context/WishlistContext';
import { SECTION_BRUSH } from '../../../../utils/cosmetics';

import './styles.scss';

export default function NotificationsView() {
  const {
    email,
    isLoading,
    isSyncing,
    isSupabaseReady,
    hasNotifications,
    saveEmail,
    clearEmail,
    syncError,
    wishlist,
  } = useWishlist();

  const [emailInput, setEmailInput] = useState(email);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setEmailInput(email);
  }, [email]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    const result = await saveEmail(emailInput);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    if (result.offline) {
      setMessage('Email saved locally. Add Supabase env vars to enable notifications.');
      return;
    }

    setMessage('Your email has been updated. We\'ll notify you when wishlisted items hit the shop.');
  }

  function handleClear() {
    setError('');
    setMessage('');
    clearEmail();
    setEmailInput('');
    setMessage('Notifications turned off. Add an email anytime to re-enable alerts.');
  }

  return (
    <div className="notifications-view page-loading-host page-loading-host--fill">
      <header className="notifications-view-header">
        <div className="notifications-view-title-wrap">
          <h1 className="notifications-view-title">NOTIFICATIONS</h1>
          <img
            className="notifications-view-brush"
            src={SECTION_BRUSH}
            alt=""
            aria-hidden
            draggable={false}
          />
        </div>
        <p className="notifications-view-subtitle">
          Update your email to get alerts when wishlisted items return to the shop.
        </p>
      </header>

      <div className="page-content-area">
        {isLoading ? (
          <NotificationsSkeleton />
        ) : (
          <>
            <div className="notifications-view-status">
              <div className="notifications-view-status-icon">
                <NotificationsOutlined />
              </div>
              <div className="notifications-view-status-copy">
                <p className="notifications-view-status-label">
                  {hasNotifications ? 'Notifications active' : 'Notifications off'}
                </p>
                <p className="notifications-view-status-detail">
                  {hasNotifications
                    ? `Alerts will be sent to ${email}.`
                    : 'Add your email below to start receiving shop alerts.'}
                </p>
              </div>
              <span className={hasNotifications ? 'notifications-view-pill active' : 'notifications-view-pill'}>
                {hasNotifications ? 'ON' : 'OFF'}
              </span>
            </div>

            <section className="notifications-view-panel">
              <h2 className="notifications-view-panel-title">Email address</h2>
              <p className="notifications-view-panel-desc">
                We&apos;ll only use this to notify you about items on your wishlist
                {wishlist.length > 0 ? ` (${wishlist.length} item${wishlist.length === 1 ? '' : 's'})` : ''}.
              </p>

              <form className="notifications-view-form" onSubmit={handleSubmit}>
                <label className="notifications-view-field" htmlFor="notifications-email">
                  <span>Email</span>
                  <input
                    id="notifications-email"
                    className="notifications-view-input"
                    type="email"
                    placeholder="your@email.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    required
                  />
                </label>

                <div className="notifications-view-actions">
                  <button type="submit" className="notifications-view-submit" disabled={isSyncing}>
                    {isSyncing ? 'SAVING...' : hasNotifications ? 'UPDATE EMAIL' : 'SAVE EMAIL'}
                  </button>
                  {hasNotifications && (
                    <button
                      type="button"
                      className="notifications-view-clear"
                      onClick={handleClear}
                      disabled={isSyncing}
                    >
                      TURN OFF
                    </button>
                  )}
                </div>
              </form>

              {error && <p className="notifications-view-error">{error}</p>}
              {message && <p className="notifications-view-success">{message}</p>}
              {syncError && <p className="notifications-view-error">{syncError}</p>}
              {!isSupabaseReady && (
                <p className="notifications-view-note">
                  Supabase not configured. Email will be saved locally only.
                </p>
              )}
            </section>

            <footer className="notifications-view-footer">
              Alerts run daily after the shop resets (00:05 UTC). You&apos;ll get one email
              per wishlist item the first time it appears in a shop rotation.
            </footer>
          </>
        )}
      </div>

      {isLoading && (
        <PageLoading message="Fetching your notification settings." />
      )}
    </div>
  );
}
